import { Instance, isAlive } from 'mobx-state-tree';
import { DistanceUnit } from './DistanceUnitEnum';
import { RoutesStatsDetailDtoModelType } from '.';
import getAppRoot from '../utils/getAppRoot';
import { RoutesStatsDtoModelBase } from './RoutesStatsDtoModel.base';

/* The TypeScript type of an instance of RoutesStatsDtoModel */
export interface RoutesStatsDtoModelType extends Instance<typeof RoutesStatsDtoModel.Type> {}

/* A graphql query fragment builders for RoutesStatsDtoModel */
export { selectFromRoutesStatsDto, routesStatsDtoModelPrimitives, RoutesStatsDtoModelSelector } from './RoutesStatsDtoModel.base';

/**
 * RoutesStatsDtoModel
 *
 * Fleet Utilization
 */
export const RoutesStatsDtoModel = RoutesStatsDtoModelBase
  .views((self) => ({
    get solutionType(): RoutesStatsDetailDtoModelType {
      return getAppRoot(self).ui.showPublishedRoutes ? self.published : self.active;
    },
  }))
  .views((self) => ({
    get workingTimeMinutes(): number {
      return self.solutionType.workingTimeMinutes || 0;
    },
    get totalDuration(): number {
      return self.solutionType.totalDuration || 0;
    },
    get totalCapacity(): number {
      return self.solutionType.totalCapacity || 0;
    },
    get load(): number {
      return self.solutionType.load || 0;
    },
  }))
  .views((self) => ({
    get isOverCapacity(): boolean {
      if (!isAlive(self)) return false;
      return self.solutionType?.isOverCapacity || false;
    },
    get hasUnpublishedChanges(): boolean {
      if (!isAlive(self)) return false;
      return self.hasUnpublished || false;
    },
    get isOvertime(): boolean {
      if (!isAlive(self)) return false;
      return self.solutionType?.isOvertime || false;
    },
    get totalStopsCount(): number {
      return self.solutionType?.stopsCount || 0;
    },
    get totalOrdersCount(): number {
      return self.solutionType?.ordersCount || 0;
    },
    get totalRoutesCount(): number {
      return self.solutionType?.routesCount || 0;
    },
    get totalDistance(): number {
      return getAppRoot(self).organization.settings.distanceUnit === DistanceUnit.KM ? self.solutionType?.kmsFromDistance
        || 0 : self.solutionType?.milesFromDistance || 0;
    },
    get shortTotalDistance(): number {
      return getAppRoot(self).organization.settings.distanceUnit === DistanceUnit.KM ? self.solutionType?.shortKmsFromDistance
        || 0 : self.solutionType?.shortMilesFromDistance || 0;
    },
    get distanceUnit(): any {
      return getAppRoot(self).organization.settings.distanceUnit;
    },
    get durationMinutes(): number | undefined | null {
      if (typeof self.totalDuration === 'number') {
        return self.totalDuration / 60;
      }
      return self.totalDuration;
    },
    get remainingWorkingTime(): number | undefined {
      const { durationMinutes } = this;
      const { workingTimeMinutes } = self;

      if (workingTimeMinutes !== undefined && durationMinutes) {
        // Minimum value to display for remaining is 0
        return Math.max(0, durationMinutes - workingTimeMinutes);
      }
      return undefined;
    },
    get percentWorkingTime(): number | undefined {
      const { durationMinutes } = this;
      const { workingTimeMinutes } = self;

      if (workingTimeMinutes && durationMinutes) {
        /**
         * Values smaller than 1% would otherwise be hidden in visualizations
         * so 1 will be the smallest value available.
         */
        return Math.ceil((workingTimeMinutes / durationMinutes) * 100);
      }
      return undefined;
    },
    get remainingCapacity(): number | undefined {
      const totalLoad = self.load;
      const { totalCapacity } = self;

      if (totalLoad !== undefined && totalCapacity) {
        // Minimum value to display for remaining is 0
        return Math.max(0, totalCapacity - totalLoad);
      }
      return undefined;
    },
    get percentLoad(): number | undefined {
      const totalLoad = self.load;
      const { totalCapacity } = self;
      if (totalLoad && totalCapacity) {
        /**
         * Values smaller than 1% would otherwise be hidden in visualizations
         * so 1 will be the smallest value available.
         */
        return Math.ceil((totalLoad / totalCapacity) * 100);
      }
      return undefined;
    },
  }))
  .actions(self => ({
    // This is an auto-generated example action.
    log() {
      console.log(JSON.stringify(self));
    },
  }));
